import { ensureOrgShifts } from "../../../../../../../services/useOrgData";
import React, { useState, useEffect, useMemo } from "react";
import { Box, Stack } from "@mui/material";
import { useNavigate } from "react-router";
import Topbar from "../../../../../../../component/TopBar";
import MainBox from "../../../../../container/MainBox";
import BtnPrimary from "../../../../../../../component/PrimaryButton";
import DataTable from "../../../../../../../component/DataTable";
import { SearchInput, StatCard } from "../../../../../../../component/listing";
import { formatDateByDay } from "../../../../../../../utils/formatDate";

import { calculateTime } from "../../../../../../../utils/calculateTime";
import { getLocationNameFromLegalUnit } from "../../../../../../../utils/getLocationNameFromLegalUnit";
import { useNotify } from "../../../../../../../component/NotificationProvider";
import { tokens } from "../../../../../../../theme";

const getShiftTime = (days = []) => {
  for (const day of days) {
    if (day.minFullDayHours && day.minFullDayHours !== 0) return `${day.minFullDayHours} hrs`;
    if (day.startTime && day.startTime !== 0 && day.endTime !== 0)
      return `${calculateTime(day.startTime)} - ${calculateTime(day.endTime)}`;
  }
  return "—";
};

function ShiftListing() {
  const navigate = useNavigate();
  const notify = useNotify();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const list = await ensureOrgShifts();
      setData(Array.isArray(list) ? [...list].reverse() : []);
    } catch (err) {
      setError(true);
      notify.error(err?.message || "Failed to load shifts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((s) =>
      (s.name || "").toLowerCase().includes(q) || (s.code || "").toLowerCase().includes(q)
    );
  }, [data, search]);

  const openEdit = (id) => navigate("/app/shift/edit", { state: { shiftId: id } });

  return (
    <Box>
      <Topbar MainHeading={"Shift Scheduler"} />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: "1.5rem" }}>
        <StatCard label="Total shifts" value={data.length} />
      </Stack>

      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ mb: "1.25rem" }}>
          <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search shift name or code" />
          <BtnPrimary to="/app/shift/new/details" sx={{ borderRadius: "8px", whiteSpace: "nowrap" }}>
            + Create Shift
          </BtnPrimary>
        </Stack>

        <DataTable
          rows={filtered}
          loading={loading}
          error={error}
          onRetry={fetchData}
          errorTitle="Couldn't load shifts"
          empty={{
            title: data.length === 0 ? "No shifts yet" : "No matches",
            description:
              data.length === 0
                ? "Create your first shift to define working hours and schedules."
                : "Try a different search.",
          }}
          onRowClick={(s) => openEdit(s._id)}
          tableSx={{ width: "100%", tableLayout: "fixed" }}
          columns={[
            { key: "code", label: "Code", render: (s) => s.code || "—" },
            {
              key: "name",
              label: "Shift Name",
              cellSx: { fontWeight: 600, color: tokens.accent },
              render: (s) => s.name || "—",
            },
            { key: "time", label: "Shift Time", render: (s) => getShiftTime(s.days) },
            {
              key: "location",
              label: "Location",
              cellSx: { color: tokens.textMuted },
              render: (s) => getLocationNameFromLegalUnit(s.legalUnit) || "—",
            },
            {
              key: "createdAt",
              label: "Created On",
              cellSx: { color: tokens.textMuted },
              render: (s) => formatDateByDay(s.createdAt),
            },
          ]}
        />

      </MainBox>
    </Box>
  );
}

export default ShiftListing;
